import React, { useState } from "react";
import axios from "axios";
import { Button } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const FileUploader = ({
  url,
  urlType = "image",
  onFileUpload,
  multiple = false,
  showPreview = true,
  buttonText,
  variant = "outlined",
  color = "primary",
  type = "blog_image",
  onInvalidFile,
  requiredWidth,
  requiredHeight,
}) => {
  const [preview, setPreview] = useState(url || "");
  const [uploading, setUploading] = useState(false);
  let API_BASE_URL = "https://cms.vosmos.world";
  if (process.env.NODE_ENV === "development") {
    API_BASE_URL = "http://localhost:5013";
  }
  // const API_BASE_URL = "http://localhost:5013";

  const IMAGE_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";
  const ALLOWED_IMAGE_MIME = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
  ];
  const ALLOWED_IMAGE_EXT = [".png", ".jpg", ".jpeg", ".webp"];
  const isValidImageFile = (file) => {
    if (!file) return false;
    const mimeOk = ALLOWED_IMAGE_MIME.includes(file.type);
    const name = (file.name || "").toLowerCase();
    const extOk = ALLOWED_IMAGE_EXT.some((ext) => name.endsWith(ext));
    return mimeOk && extOk;
  };
  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (multiple) {
      for (let i = 0; i < files.length; i++) {
        if (!isValidImageFile(files[i])) {
          onInvalidFile && onInvalidFile("Image format not supported");
          return;
        }
      }
    } else {
      if (!isValidImageFile(files[0])) {
        onInvalidFile && onInvalidFile("Image format not supported");
        return;
      }
    }
    // Validate dimensions if required
    const validateDimensions = (file) =>
      new Promise((resolve, reject) => {
        if (!requiredWidth || !requiredHeight) {
          resolve(true);
          return;
        }
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          const isOk =
            img.width === requiredWidth && img.height === requiredHeight;
          URL.revokeObjectURL(objectUrl);
          if (isOk) resolve(true);
          else
            reject(`Image must be ${requiredWidth}x${requiredHeight} pixels`);
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject("Unable to read image dimensions");
        };
        img.src = objectUrl;
      });

    try {
      if (multiple) {
        for (let i = 0; i < files.length; i++) {
          // eslint-disable-next-line no-await-in-loop
          await validateDimensions(files[i]);
        }
      } else {
        await validateDimensions(files[0]);
      }
    } catch (dimensionError) {
      onInvalidFile && onInvalidFile(String(dimensionError));
      return;
    }

    const formData = new FormData();
    formData.append("type", type);

    if (multiple) {
      for (let i = 0; i < files.length; i++) {
        formData.append("image", files[i]);
      }
    } else {
      formData.append("image", files[0]);
    }

    try {
      setUploading(true);
      const response = await axios.post(
        `${API_BASE_URL}${
          multiple ? "/api/multi-upload" : "/api/upload/upload"
        }`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.data.success) {
        if (multiple) {
          const urls = response.data.data.map((file) => file.fileSrc);
          onFileUpload(urls);
          setPreview(urls[0]);
        } else {
          const url = response.data.data.fileSrc;
          onFileUpload(url);
          setPreview(url);
        }
      }
    } catch (error) {
      console.error("Upload failed:", error);
      onInvalidFile && onInvalidFile("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {showPreview && preview && urlType === "image" && (
        <img
          src={preview}
          alt="Preview"
          style={{
            width: "160px",
            height: "auto",
            borderRadius: "8px",
            marginBottom: "8px",
          }}
        />
      )}

      <Button
        component="label"
        variant={variant}
        color={color}
        startIcon={<CloudUploadIcon />}
        disabled={uploading}
        sx={{ m: 0 }}
      >
        {uploading ? "Uploading..." : buttonText || "Upload Image"}
        <input
          type="file"
          accept={urlType === "image" ? IMAGE_ACCEPT : "*/*"}
          multiple={multiple}
          hidden
          onChange={handleFileChange}
        />
      </Button>
    </div>
  );
};

export default FileUploader;
